import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { unwrapPaginated } from "@/config/api";
import { getApiErrorMessage } from "@/lib/apiError";
import type { ModeratorCompany } from "@/services/moderators/generated/model";
import { useModeratorsCompaniesList } from "@/services/moderators/generated/moderators-companies/moderators-companies";
import { useModeratorsDealsCreateManualCreate } from "@/services/moderators/generated/moderators-deals/moderators-deals";
import "./ModeratorArticlesPage.css";

/**
 * OQ-12 — link two approved companies that were never matched. A reason is
 * required; the deal is flagged "Manual" everywhere moderators see deals.
 */
const ManualDealModal = ({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) => {
  const { t } = useTranslation(["deals", "common"]);
  const navigate = useNavigate();
  const [buyerId, setBuyerId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [reason, setReason] = useState("");
  const [title, setTitle] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data } = useModeratorsCompaniesList({ page_size: 100 });
  const companies = unwrapPaginated<ModeratorCompany>(data)
    .filter((c) => c.isApproved && c.isActive)
    .sort((a, b) => a.companyName.localeCompare(b.companyName));
  const createMutation = useModeratorsDealsCreateManualCreate();

  const submit = () => {
    if (!buyerId || !supplierId) {
      setError(t("manualDeal.errors.pickBoth"));
      return;
    }
    if (buyerId === supplierId) {
      setError(t("manualDeal.errors.pickDifferent"));
      return;
    }
    if (!reason.trim()) {
      setError(t("manualDeal.errors.reasonRequired"));
      return;
    }
    createMutation.mutate(
      {
        data: {
          buyerCompanyId: Number(buyerId),
          supplierCompanyId: Number(supplierId),
          reason: reason.trim(),
          ...(title.trim() ? { requirementTitle: title.trim() } : {}),
        },
      },
      {
        onSuccess: (deal) => {
          onCreated();
          navigate(`/moderator/deals/${deal.id}`);
        },
        onError: (err) => setError(getApiErrorMessage(err, t("manualDeal.errors.createFailed"))),
      },
    );
  };

  const companyOptions = companies.map((c) => (
    <option key={c.id} value={c.id}>
      {c.companyName}
      {c.country ? ` (${c.country})` : ""}
    </option>
  ));

  return (
    <div className="mod-modal__overlay" onClick={onClose}>
      <div className="mod-modal__content" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <header className="mod-modal__header">
          <h2 className="mod-modal__title">{t("manualDeal.title")}</h2>
          <button type="button" className="mod-modal__close" onClick={onClose} aria-label={t("common:actions.close")}>
            <i className="fa-solid fa-xmark" />
          </button>
        </header>
        <div className="mod-modal__form">
          <p className="mod-page__card-meta">{t("manualDeal.intro")}</p>
          <div className="mod-modal__row">
            <label className="mod-modal__label">
              {t("manualDeal.buyer")}
              <select className="mod-page__select" value={buyerId} onChange={(e) => setBuyerId(e.target.value)}>
                <option value="">{t("manualDeal.selectCompany")}</option>
                {companyOptions}
              </select>
            </label>
            <label className="mod-modal__label">
              {t("manualDeal.supplier")}
              <select className="mod-page__select" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
                <option value="">{t("manualDeal.selectCompany")}</option>
                {companyOptions}
              </select>
            </label>
          </div>
          <label className="mod-modal__label">
            {t("manualDeal.reason")}
            <textarea
              className="mod-page__input"
              rows={3}
              maxLength={2000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("manualDeal.reasonPlaceholder")}
            />
          </label>
          <label className="mod-modal__label">
            {t("manualDeal.dealTitle")}
            <input
              type="text"
              className="mod-page__input"
              maxLength={255}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
          {error ? <p className="mod-modal__error">{error}</p> : null}
          <div className="mod-modal__footer">
            <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={onClose}>
              {t("common:actions.cancel")}
            </button>
            <button
              type="button"
              className="mod-page__btn mod-page__btn--primary"
              disabled={createMutation.isPending}
              onClick={submit}
            >
              {createMutation.isPending ? t("manualDeal.creating") : t("manualDeal.title")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualDealModal;
